import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { 
  Brain, AlertTriangle, TrendingUp, Activity, RefreshCw, 
  CheckCircle2, XCircle, Clock, Zap, Target, BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SmartAnalytics = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  const [analysisType, setAnalysisType] = useState('all');
  const [predictionMetric, setPredictionMetric] = useState('nrw');
  const [summary, setSummary] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [error, setError] = useState(null);

  const loadHealthScore = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/smart-analytics/health-score`);
      const data = await response.json();
      if (data.success) {
        setHealthScore(data);
      }
    } catch (err) {
      console.error('Failed to load health score:', err);
    }
  }, []);

  useEffect(() => {
    loadHealthScore();
  }, [loadHealthScore]);

  const runAnomalyDetection = async () => {
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/smart-analytics/anomalies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_type: analysisType })
      });
      const data = await response.json();
      if (data.success) {
        setAnomalies(data.anomalies || []);
        setSummary(data.summary || '');
        setRecommendations(data.recommendations || []);
      } else {
        setError(data.detail || 'Analysis failed');
      }
    } catch (err) {
      setError('Failed to run analysis. Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const runPrediction = async () => {
    setIsPredicting(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/smart-analytics/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metric: predictionMetric, days_ahead: 7 })
      });
      const data = await response.json();
      if (data.success) {
        setPredictions(data.predictions || []);
      } else {
        setError(data.detail || 'Prediction failed');
      }
    } catch (err) {
      setError('Failed to generate predictions. Please try again.');
      console.error('Prediction error:', err);
    } finally {
      setIsPredicting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-success/10 text-success border-success/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-blue-500';
      case 'fair': return 'text-warning';
      case 'poor': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
          <Brain className="w-8 h-8 text-primary" />
          Smart Analytics
        </h1>
        <p className="text-muted-foreground">AI-powered anomaly detection and predictive analytics for water supply monitoring</p>
      </div>

      {/* System Health Score Card */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <Activity className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">System Health Score</p>
                {healthScore ? (
                  <>
                    <p className={`text-4xl font-bold ${getHealthStatusColor(healthScore.status)}`}>
                      {healthScore.overall_health}%
                    </p>
                    <p className="text-sm capitalize">{healthScore.status}</p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-muted-foreground">Loading...</p>
                )}
              </div>
            </div>
            {healthScore && (
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-background rounded-lg">
                  <p className="text-xs text-muted-foreground">NRW Score</p>
                  <p className="text-lg font-bold">{healthScore.components?.nrw_score}%</p>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <p className="text-xs text-muted-foreground">Quality</p>
                  <p className="text-lg font-bold">{healthScore.components?.quality_score}%</p>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <p className="text-xs text-muted-foreground">Flow Efficiency</p>
                  <p className="text-lg font-bold">{healthScore.components?.flow_efficiency}%</p>
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={loadHealthScore}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anomaly Detection Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              AI Anomaly Detection
            </CardTitle>
            <CardDescription>
              Detect unusual patterns in water flow, NRW spikes, and quality issues
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Select value={analysisType} onValueChange={setAnalysisType}>
                <SelectTrigger className="w-40" data-testid="analysis-type-select">
                  <SelectValue placeholder="Analysis Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Data</SelectItem>
                  <SelectItem value="flow">Flow Data</SelectItem>
                  <SelectItem value="nrw">NRW Data</SelectItem>
                  <SelectItem value="quality">Quality Data</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={runAnomalyDetection} 
                disabled={isAnalyzing}
                data-testid="run-analysis-btn"
                className="flex-1"
              >
                {isAnalyzing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Run AI Analysis
                  </>
                )}
              </Button>
            </div>

            {isAnalyzing && (
              <div className="space-y-2">
                <Progress value={66} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">AI analyzing water supply data...</p>
              </div>
            )}

            {summary && !isAnalyzing && (
              <Alert>
                <Brain className="h-4 w-4" />
                <AlertDescription>{summary}</AlertDescription>
              </Alert>
            )}

            {anomalies.length > 0 && !isAnalyzing && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {anomalies.map((anomaly, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${getSeverityColor(anomaly.severity)}`}
                    data-testid={`anomaly-item-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-medium text-sm">{anomaly.type}</span>
                      <Badge variant="outline" className={getSeverityColor(anomaly.severity)}>
                        {anomaly.severity}
                      </Badge>
                    </div>
                    <p className="text-xs mb-1">{anomaly.description}</p>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>Location: {anomaly.location}</span>
                      <span>Value: {anomaly.value}</span>
                      <span>Expected: {anomaly.expected}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {recommendations.length > 0 && !isAnalyzing && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  AI Recommendations
                </p>
                <ul className="space-y-1">
                  {recommendations.map((rec, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Predictive Analytics Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Predictive Analytics
            </CardTitle>
            <CardDescription>
              AI-powered forecasting for water supply metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Select value={predictionMetric} onValueChange={setPredictionMetric}>
                <SelectTrigger className="w-40" data-testid="prediction-metric-select">
                  <SelectValue placeholder="Metric" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nrw">NRW %</SelectItem>
                  <SelectItem value="demand">Demand (MLD)</SelectItem>
                  <SelectItem value="supply">Supply (MLD)</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={runPrediction} 
                disabled={isPredicting}
                data-testid="run-prediction-btn"
                className="flex-1"
              >
                {isPredicting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Predicting...
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Generate Forecast
                  </>
                )}
              </Button>
            </div>

            {isPredicting && (
              <div className="space-y-2">
                <Progress value={50} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">Generating 7-day forecast...</p>
              </div>
            )}

            {predictions.length > 0 && !isPredicting && (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={predictions}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="date" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={10}
                        tickFormatter={(value) => value.split('-').slice(1).join('/')}
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="predicted_value" 
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary)/0.2)"
                        name="Predicted"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {predictions.slice(0, 4).map((pred, idx) => (
                    <div key={idx} className="p-2 bg-muted rounded text-center">
                      <p className="text-xs text-muted-foreground">{pred.date}</p>
                      <p className="font-bold">{pred.predicted_value}%</p>
                      <p className="text-xs text-muted-foreground">
                        Conf: {Math.round((pred.confidence || 0.8) * 100)}%
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Brain className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{anomalies.length}</p>
            <p className="text-xs text-muted-foreground">Anomalies Detected</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-destructive" />
            <p className="text-2xl font-bold">{anomalies.filter(a => a.severity === 'high').length}</p>
            <p className="text-xs text-muted-foreground">High Severity</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-warning" />
            <p className="text-2xl font-bold">{anomalies.filter(a => a.severity === 'medium').length}</p>
            <p className="text-xs text-muted-foreground">Medium Severity</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-success" />
            <p className="text-2xl font-bold">{recommendations.length}</p>
            <p className="text-xs text-muted-foreground">Recommendations</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SmartAnalytics;
